import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Check, Clock, ChefHat, Package, Bike, CheckCircle2, XCircle,
  Loader2, Bell, BellRing, Store,
} from 'lucide-react';
import { registerPush, isPushSupported, isPushSubscribed } from '../services/push';

const API = process.env.REACT_APP_BACKEND_URL;
const ORANGE = '#D44E20';
const POLL_MS = 60000; // 60s — enough for an order, saves resources

const STEP_META = {
  received: { label: 'Ricevuto', sub: 'Ordine ricevuto', Icon: Check },
  confirmed: { label: 'Confermato', sub: 'Il locale ha confermato', Icon: CheckCircle2 },
  preparing: { label: 'In preparazione', sub: 'Stiamo cucinando', Icon: ChefHat },
  ready: { label: 'Pronto', sub: 'Pronto per te', Icon: Package },
  out_for_delivery: { label: 'In consegna', sub: 'Il rider è in strada', Icon: Bike },
  delivered: { label: 'Consegnato', sub: 'Completato', Icon: CheckCircle2 },
};

function fmtTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

export default function OrderTrackingPage() {
  const { orderId: paramId } = useParams();
  const [sp] = useSearchParams();
  const orderId = paramId || sp.get('order_id');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const prevStatus = useRef(null);

  const load = useCallback(async () => {
    if (!orderId) { setError(true); setLoading(false); return; }
    try {
      const r = (await axios.get(`${API}/api/public/orders/${orderId}/track`)).data;
      if (prevStatus.current && prevStatus.current !== r.order_status) {
        const m = STEP_META[r.order_status];
        if (m) toast.success(`${m.label} — ${m.sub}`, { position: 'top-center' });
      }
      prevStatus.current = r.order_status;
      setData(r);
    } catch (e) {
      setError(true);
    } finally { setLoading(false); }
  }, [orderId]);

  useEffect(() => {
    load();
    const iv = setInterval(load, POLL_MS);
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => {
    if (isPushSupported()) isPushSubscribed().then(setPushOn);
  }, []);

  const enablePush = async () => {
    if (!data?.customer_id) return;
    setPushBusy(true);
    try {
      const sub = await registerPush(data.customer_id);
      if (sub) { setPushOn(true); toast.success('Notifiche attivate! Ti avviseremo ad ogni aggiornamento.'); }
      else toast.error('Non è stato possibile attivare le notifiche.');
    } finally { setPushBusy(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="animate-spin" style={{ color: ORANGE }} /></div>;
  }
  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-6 text-center" data-testid="track-error">
        <XCircle size={40} className="text-slate-300 mb-3" />
        <p className="font-bold text-slate-700">Ordine non trovato</p>
        <p className="text-sm text-slate-500 mt-1">Controlla il link o riprova più tardi.</p>
      </div>
    );
  }

  const steps = data.steps || [];
  const currentIdx = data.cancelled ? -1 : steps.indexOf(data.order_status);
  const isDelivery = data.order_type === 'delivery';
  const done = data.order_status === 'delivered';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="order-tracking-page">
      <header className="bg-white border-b border-slate-100 px-4 py-4 sticky top-0 z-20">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide">Ordine</p>
            <p className="font-extrabold text-slate-900" data-testid="track-order-number">#{data.order_number}</p>
          </div>
          <div className="flex items-center gap-1.5 text-slate-500 text-sm font-semibold">
            {isDelivery ? <Bike size={16} /> : <Store size={16} />}
            {isDelivery ? 'Consegna' : 'Ritiro'}
          </div>
        </div>
      </header>

      <div className="max-w-md mx-auto p-4 space-y-4">
        {/* Hero status */}
        <div className="rounded-2xl p-5 text-white" style={{ background: `linear-gradient(135deg, ${ORANGE}, #F59E0B)` }} data-testid="track-hero">
          {data.cancelled ? (
            <>
              <p className="text-lg font-extrabold">Ordine annullato</p>
              <p className="text-sm opacity-90 mt-1">Questo ordine è stato annullato.</p>
            </>
          ) : done ? (
            <>
              <p className="text-2xl font-extrabold">🎉 {isDelivery ? 'Consegnato!' : 'Ritirato!'}</p>
              <p className="text-sm opacity-90 mt-1">Grazie e a presto!</p>
            </>
          ) : (
            <>
              <p className="text-sm opacity-90 font-semibold uppercase tracking-wide">{STEP_META[data.order_status]?.label || 'In corso'}</p>
              <p className="text-lg font-extrabold mt-0.5">{STEP_META[data.order_status]?.sub || ''}</p>
              {data.eta_at && (
                <div className="mt-3 inline-flex items-center gap-1.5 bg-white/20 rounded-full px-3 py-1.5 text-sm font-bold" data-testid="track-eta">
                  <Clock size={15} /> {isDelivery ? 'Consegna stimata' : 'Pronto'} entro le {fmtTime(data.eta_at)}
                </div>
              )}
              {isDelivery && data.rider_name && data.order_status === 'out_for_delivery' && (
                <p className="text-sm mt-2 opacity-90">🛵 In consegna con <b>{data.rider_name}</b></p>
              )}
            </>
          )}
        </div>

        {/* Push opt-in */}
        {isPushSupported() && data.customer_id && !done && !data.cancelled && (
          pushOn ? (
            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 rounded-xl p-3" data-testid="track-push-on">
              <BellRing size={16} /> Notifiche attive: ti avviseremo ad ogni aggiornamento.
            </div>
          ) : (
            <button onClick={enablePush} disabled={pushBusy} data-testid="track-push-enable"
              className="w-full flex items-center justify-center gap-2 rounded-xl py-3 font-bold text-white" style={{ backgroundColor: ORANGE }}>
              {pushBusy ? <Loader2 size={16} className="animate-spin" /> : <Bell size={16} />}
              Avvisami quando cambia stato
            </button>
          )
        )}

        {/* Timeline */}
        {!data.cancelled && (
          <div className="bg-white rounded-2xl p-5" data-testid="track-timeline">
            {steps.map((s, i) => {
              const meta = STEP_META[s] || { label: s, sub: '', Icon: Clock };
              const reached = i <= currentIdx;
              const isCurrent = i === currentIdx;
              const Ic = meta.Icon;
              return (
                <div key={s} className="flex gap-3" data-testid={`track-step-${s}`}>
                  <div className="flex flex-col items-center">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${reached ? 'text-white' : 'bg-slate-100 text-slate-300'}`}
                      style={reached ? { backgroundColor: ORANGE } : {}}>
                      <Ic size={17} />
                    </div>
                    {i < steps.length - 1 && (
                      <div className="w-0.5 flex-1 my-1" style={{ minHeight: 24, backgroundColor: i < currentIdx ? ORANGE : '#e2e8f0' }} />
                    )}
                  </div>
                  <div className={`pb-4 ${isCurrent ? '' : 'opacity-90'}`}>
                    <p className={`font-bold text-sm ${reached ? 'text-slate-900' : 'text-slate-400'}`}>{meta.label}</p>
                    <p className="text-xs text-slate-500">{meta.sub}</p>
                    {isCurrent && <span className="inline-block mt-1 text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FFF0EA', color: ORANGE }}>In corso</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Order summary */}
        <div className="bg-white rounded-2xl p-5" data-testid="track-summary">
          <p className="text-xs font-semibold text-slate-400 uppercase mb-3">Il tuo ordine</p>
          {(data.items || []).map((it, i) => (
            <div key={i} className="flex justify-between text-sm py-1">
              <span className="text-slate-700">{it.quantity}× {it.name}</span>
            </div>
          ))}
          {data.delivery_fee > 0 && (
            <div className="flex justify-between text-sm py-1 text-slate-500 border-t border-slate-100 mt-2 pt-2">
              <span>Consegna</span><span>{Number(data.delivery_fee).toFixed(2)}€</span>
            </div>
          )}
          <div className="flex justify-between font-bold text-base border-t border-slate-100 mt-2 pt-2">
            <span>Totale</span><span style={{ color: ORANGE }}>{Number(data.total).toFixed(2)}€</span>
          </div>
          {data.payment_method === 'cod' && (
            <p className="text-xs text-slate-400 mt-2">Pagamento in contanti al ritiro.</p>
          )}
        </div>
      </div>
    </div>
  );
}
